import { useAppDispatch } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { WordSlideData } from "@ai-teach/types";
import type { MediaInput, SlideHandlers } from "../types";

/**
 * Handlers for word slide type
 * Word slides have a fixed layout with specific fields (word, image, audio, syllables)
 * They don't use the canvas element system like other slide types
 */
export const useWordHandlers = (slide: SlideFields | null): SlideHandlers => {
  const dispatch = useAppDispatch();

  const wordSlideData = slide?.slideData as WordSlideData | undefined;

  if (!slide || !wordSlideData) {
    // Return no-op functions if no slide data
    return {
      addImage: () => {},
      addAudio: () => {},
      addText: () => {},
    };
  }

  const slideId = slide.id;

  /**
   * Add image to word slide
   * Updates the imageId property in slideData
   */
  const addImage = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({ slideId, slideData: { ...wordSlideData, image: { src, id, filename } } })
    );
  };

  /**
   * Add audio to word slide
   * Updates the wordAudioId property in slideData
   */
  const addAudio = ({ src, id, filename }: MediaInput) => {
    dispatch(
      updateSlide({ slideId, slideData: { ...wordSlideData, wordAudio: { src, id, filename } } })
    );
  };

  /**
   * Update the word text
   */
  const addText = (text: string) => {
    dispatch(updateSlide({ slideId, slideData: { ...wordSlideData, word: text } }));
  };

  return {
    addImage,
    addAudio,
    addText,
  };
};
